import { debounce, infiniteScroll } from '../utils';

/**
 * Post listing
 * 
 * @param {HTMLElement} ele
 * @param {object} opts
 * @returns {null}
 */
export const postListing = (ele, opts = {}) => {
  const {
    buildQueryUrl,
    postHtml,
    perPage = 10,
    queryChangeEvent,
    initialQuery,
    onLoaderShow,
    beforePostsLoad,
    afterPostsLoad,
    afterPostsRender
  } = opts;

  let 
    _posts = [],
    initialised = false,
    loading = false,
    end = false,
    page = 1,
    query = initialQuery || {};

  const postsEle = ele.querySelector('.posts');
  const loadingEle = ele.querySelector('.loading');
  const messageEle = ele.querySelector('.message')

  const nextPage = () => {
    if (loading || end || !initialised) return;

    page++;

    getAndRenderPosts({...query, page});
  }

  const getAndRenderPostsRequest = debounce(async (query) => {
    beforePostsLoad && beforePostsLoad({query});

    try {
      const url = buildQueryUrl(query);
      const res = await fetch(url);
      let posts = await res.json();

      if (!posts.length || posts.length < perPage) end = true;

      afterPostsLoad && afterPostsLoad({posts, query});
      renderPosts(posts);
    } catch (err) {
      console.error(err);
      end = true;

      setMessage({
        title: 'Error',
        copy: 'There was an error loading posts.'
      });
    } finally {
      setLoading(false);
    }
  }, 1000);

  const getAndRenderPosts = async (query) => {
    setLoading(true);
    setMessage({});

    getAndRenderPostsRequest(query);
  }

  const renderPosts = (posts) => {
    let html = '';

    if (!posts || !posts.length) {
      setMessage({
        title: 'No resources found'
      });
    } else {
      posts.forEach(post => {
        const index = _posts.push(post);
        html += postHtml(post, {index});
      });
    }

    postsEle.innerHTML += html;
    afterPostsRender && afterPostsRender({posts});
  }

  const onQueryChange = (newQuery) => {
    query = newQuery;
    page = 1;
    end = false;
    _posts = [];

    postsEle.innerHTML = '';

    getAndRenderPosts({...query, page});
  }

  const setLoading = (bool) => {
    loading = bool;
    loadingEle.classList.toggle('hidden', !bool);
    loading && onLoaderShow && onLoaderShow();
  }

  const setMessage = ({ title, copy }) => {
    let html = '';

    if (title) html += `<h3>${title}</h3>`;
    if (copy) html += `<p>${copy}</p>`;
    
    messageEle.innerHTML = html;
    messageEle.classList.toggle('hidden', !html);
  }

  const init = () => {
    getAndRenderPosts({...query, page});
    infiniteScroll(ele, nextPage);

    if (queryChangeEvent) {
      document.addEventListener(queryChangeEvent, ({ detail: { query } }) => onQueryChange(query));
    }

    initialised = true;
  }

  init();
}

/**
 * Post filters
 * 
 * @param {object} opts
 * @returns {object}
 */
export const postFilters = (opts = {}) => {
  const {
    queryChangeEvent,
    fields,
    arrayFields,
    initialQuery
  } = opts;

  let query;

  const setQuery = (newQuery) => {
    query = filterQuery(newQuery);
    dispatchChangeEvent(query);
    setUrlQuery(query);
  }

  const getQuery = () => {
    return query;
  }

  const dispatchChangeEvent = (query) => {
    if (!queryChangeEvent) return;

    document.dispatchEvent(new CustomEvent(queryChangeEvent, {
      detail: { query }
    }));
  }

  const setUrlQuery = (query) => {
    const url = new URL(window.location.href);

    fields.forEach(name => {
      let value = query[name];

      if (Array.isArray(value)) value = value.join(',');
  
      value
        ? url.searchParams.set(name, value)
        : url.searchParams.delete(name);
    });

    window.history.replaceState({}, '', url);
  }

  const getUrlQuery = () => {
    const url = new URL(window.location.href);
    let urlQuery = {};

    fields.forEach(name => {
      let value = url.searchParams.get(name);

      if (value && arrayFields.includes(name)) {
        value = value.split(',');
      }

      if (value === 'true') value = true;
      if (value === 'false') value = false;

      if (value) {
        urlQuery[name] = value;
      }
    });

    return urlQuery;
  }

  const filterQuery = (query) => { 
    const validQuery = {};

    fields.forEach(field => {
      const value = query[field];

      if (!value) return;

      if (arrayFields.includes(field)) {
        if (Array.isArray(value) && value.length) validQuery[field] = value;
      } else if (Array.isArray(value)) {
        validQuery[field] = value.join(',');
      } else {
        validQuery[field] = value;
      }
    });

    return validQuery;
  }

  window.resetResourceFilters = () => {
    setQuery({});
  }

  const init = () => {
    const urlQuery = getUrlQuery();

    query = Object.keys(urlQuery).length !== 0 
      ? urlQuery 
      : initialQuery ? filterQuery(initialQuery) : {};
  }

  init();

  return {
    setQuery,
    getQuery
  }
}